import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { URLS } from '../../../data/navigation/navigation.data';
import { findStoreListing, isFreeSku, storeProducts } from '../../../data/product/product-lookup.data';
import { Platform, Product } from '../../../model/product/product.model';
import { CartPopupService } from '../../../service/cart/cart-popup.service';
import { StoreService } from '../../../service/store/store.service';

/** a listing sold on the site that goes with what is in the cart, and why */
export interface StoreSuggestion {
  sku: string;
  product: Product;
  category: string;
  /** "Ocean Theme" */
  name: string;
  platformName: string;
  platformLogo: string;
  listPrice?: { amount: number; currency: string };
  /** the theme's three tags, as soft chips (owner, 2026-09-20: the tags only, no category / mode line, never colours) */
  tags: string[];
  /** why it is here, in a few words: "Complete your set", "Same mode: Dark", "Same category: Nature", "Same vibe: Mediterranean"... (owner, 2026-09-20) */
  reason: string;
  /** every reason the listing could give, the most telling first; the strip hands each card a kind the others do not use */
  reasons: Reason[];
  score: number;
}

/** what a shared trait is worth in the ranking */
const SAME_THEME = 6;
const SAME_COLOR = 2;
const SAME_APPEARANCE = 2;
const CLOSE_HUE = 1.5;
const SAME_CATEGORY = 1.5;
const SAME_TAG = 1;
/** the platform the visitor already buys for edges out the same theme on another one */
const SAME_PLATFORM = 0.5;
/** the reason of the same theme on another platform; always the first card when there is one */
const COMPLETE_SET = 'Complete your set';

/** a reason: its kind (two cards never share one; "Same vibe: Royal" and "Same vibe: Coastal" are one kind) and its words */
interface Reason {
  kind: 'set' | 'vibe' | 'color' | 'category' | 'mode' | 'pick';
  label: string;
}

/**
 * "You might also like": up to three listings under the cart that go with
 * the lines in it (owner decision 2026-09-20: the visitor is at the checkout,
 * so nudge them to fill the cart). A candidate is every PAID listing (never a free one, owner 2026-09-21)
 * that is not in the cart; it scores by what it shares with the cart's
 * themes: the same theme on another platform first, then the colour it reads
 * as, dark/light, a close accent hue, the category (nature, cities), the
 * tags. Only listings the store confirms it can sell are shown. An empty cart
 * gets "Recommendations": three listings drawn at random (owner, 2026-09-20:
 * not the same three on every visit), shuffled once per page view so the
 * strip holds still while the catalogue refreshes. A card: the reason, icon,
 * name, platform, the theme's three tags as soft chips, the price and Add to cart.
 *
 * Every card says why it is there (owner, 2026-09-20), from the trait that
 * earned it the most points: the same theme on another platform is "Complete
 * your set" (Sublime London in the cart suggests London for macOS Terminal),
 * then "Same mode: Dark", "Same category: Nature", "Same color",
 * "Same vibe: Mediterranean" (a shared tag: Madrid and Istanbul); a pick
 * with nothing in common, or on an empty cart, wears its editorial label
 * ("Popular", "Hidden Gem") or "Recommended".
 *
 * The three cards are three different themes (owner, 2026-09-20: never the
 * same theme twice, even on two platforms; the platform already in the cart
 * wins the tie) with three different reasons (owner: never two "Same mode: Dark"):
 * "Complete your set" always comes first when there is one (the other set
 * completions queue at the back and fill the strip only when nothing else is
 * left), then the best-scoring listing that can give a reason the strip does
 * not show yet, labelled with its most telling unused reason (a shared tag
 * before the colour, the category, the mode), the best remaining only when
 * no fresh reason is left. Three cards as long as there are candidates.
 */
@Component( {
  selector: 'app-store-suggestions',
  templateUrl: './store-suggestions.component.html',
  styleUrls: [ './store-suggestions.component.scss' ],
} )
export class StoreSuggestionsComponent implements OnInit, OnChanges, OnDestroy {

  URLS = URLS;

  /** the skus in the cart */
  @Input() skus: string[] = [];
  @Input() limit = 3;

  suggestions: StoreSuggestion[] = [];
  /** "You might also like" over a cart with lines, "Recommendations" over an empty one */
  title = '';

  /** the empty cart's shuffle, drawn once per page view (a rebuild on the catalogue's refresh keeps the order) */
  private readonly seed = Math.floor( Math.random() * 0x100000000 );

  private sub: Subscription | null = null;

  constructor(
    private storeService: StoreService,
    private cartPopupService: CartPopupService,
  ) {
  }

  ngOnInit(): void {
    // the store's answer decides which listings can be sold, so the strip follows it
    this.sub = this.storeService.catalog$.subscribe( () => this.rebuild() );
  }

  ngOnChanges(): void {
    this.rebuild();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  priceLabel( suggestion: StoreSuggestion ): string | null {
    return this.storeService.priceLabel( suggestion.sku, suggestion.listPrice );
  }

  /** the theme page's query (the name and the icon link there, in a new tab, so the cart stays) */
  themeQuery( suggestion: StoreSuggestion ): { [ key: string ]: string } {
    return { product: suggestion.product.key, category: ( suggestion.category || '' ).toLowerCase() };
  }

  add( suggestion: StoreSuggestion ): void {
    this.cartPopupService.add( suggestion.sku, 'cart-suggestions' );
  }

  trackBySku( index: number, suggestion: StoreSuggestion ): string {
    return suggestion.sku;
  }

  // ---------------------------------------------------------------- helpers

  private rebuild(): void {
    const inCart = new Set( this.skus );
    const lines = this.skus.map( sku => findStoreListing( sku ) ).filter( listing => !!listing?.product );
    this.title = lines.length ? 'You might also like' : 'Recommendations';

    const scored: StoreSuggestion[] = [];
    for ( const hit of storeProducts() ) {
      for ( const platform of hit.product.platforms || [] ) {
        // never recommend a free listing (owner, 2026-09-21): the strip only ever nudges toward a paid theme,
        // whether the cart has lines ("You might also like") or is empty ("Recommendations")
        if ( !platform.sku || isFreeSku( platform.sku ) || inCart.has( platform.sku ) || platform.status === 'planned' ) continue;
        if ( this.storeService.availability( platform.sku ) !== 'buy' ) continue;
        scored.push( this.score( hit.product, hit.category, platform, lines.map( l => l! ) ) );
      }
    }
    // over lines, the best matches; over an empty cart every listing scores alike, so the order is drawn at random
    if ( lines.length ) scored.sort( ( a, b ) => b.score - a.score );
    else shuffle( scored, seededRandom( this.seed ) );
    this.suggestions = pickVaried( scored, this.limit );
  }

  private score( product: Product, category: string, platform: Platform, lines: { product: Product | null; productKey: string; platformName: string; category: string }[] ): StoreSuggestion {
    // the points per trait: the biggest earner names the reason on the card
    const points: TraitPoints = { theme: 0, appearance: 0, category: 0, color: 0, hue: 0, tag: 0 };
    let sharedColor = '';
    let sharedTag = '';

    for ( const line of lines ) {
      const other = line.product;
      if ( !other ) continue;
      if ( other.key === product.key ) {
        points.theme += SAME_THEME;
        continue;
      }
      for ( const color of product.mainColors || [] ) {
        if ( ( other.mainColors || [] ).some( c => c.toLowerCase() === color.toLowerCase() ) ) {
          points.color += SAME_COLOR;
          sharedColor = sharedColor || color;
        }
      }
      if ( product.appearance && other.appearance && product.appearance.toLowerCase() === other.appearance.toLowerCase() ) points.appearance += SAME_APPEARANCE;
      if ( hueDistance( product.designTokens?.primaryColor, other.designTokens?.primaryColor ) <= 30 ) points.hue += CLOSE_HUE;
      if ( category && line.category === category ) points.category += SAME_CATEGORY;
      for ( const tag of product.tags || [] ) {
        if ( ( other.tags || [] ).some( t => t.toLowerCase() === tag.toLowerCase() ) ) {
          points.tag += SAME_TAG;
          sharedTag = sharedTag || tag;
        }
      }
    }
    let score = points.theme + points.appearance + points.category + points.color + points.hue + points.tag;
    // the platform the cart already holds edges out the same theme elsewhere; an editorial pick edges out an equal
    if ( lines.some( line => line.platformName === platform.info?.name ) ) score += SAME_PLATFORM;
    if ( product.editorialLabel ) score += 0.25;

    const name = /\btheme$/i.test( product.name ) ? product.name : `${ product.name } Theme`;
    return {
      sku: platform.sku!,
      product,
      category,
      name,
      platformName: platform.info?.name || '',
      platformLogo: platform.info?.logo || '',
      listPrice: platform.listPrice,
      tags: ( product.tags || [] ).slice( 0, 3 ),
      reason: '',
      reasons: reasonsFor( points, product, category, sharedColor, sharedTag ),
      score,
    };
  }
}

/**
 * The cards, from the candidates in score order: one per theme (its best
 * platform), "Complete your set" first, then the best listing that offers a
 * reason not used yet, each card labelled with the reason it was picked for.
 */
function pickVaried( scored: StoreSuggestion[], limit: number ): StoreSuggestion[] {
  const themes = new Set<string>();
  const pool = scored.filter( candidate => {
    if ( themes.has( candidate.product.key ) ) return false;
    themes.add( candidate.product.key );
    return true;
  } );
  const picked: StoreSuggestion[] = [];
  // what a card has used up: its reason's kind (an editorial pick counts by its label, so an empty
  // cart still varies "Hidden Gem" and "Designer's Choice")
  const keyOf = ( reason: Reason ) => reason.kind === 'pick' ? `pick:${ reason.label }` : reason.kind;
  const used = new Set<string>();
  const take = ( index: number, reason: Reason ) => {
    const [ next ] = pool.splice( index, 1 );
    picked.push( { ...next, reason: reason.label } );
    used.add( keyOf( reason ) );
  };
  const completesSet = ( candidate: StoreSuggestion ) => candidate.reasons.some( r => r.kind === 'set' );
  // the set first (the best-scoring listing that completes one); the other set completions go to the
  // back of the queue: they fill the strip when nothing else is left (owner, 2026-09-20: three cards
  // as long as there are candidates), with their next reason, and come forward on the next rebuild
  const set = pool.findIndex( completesSet );
  if ( set >= 0 ) {
    take( set, pool[ set ].reasons.find( r => r.kind === 'set' )! );
    const others = pool.filter( completesSet );
    for ( const other of others ) pool.splice( pool.indexOf( other ), 1 );
    pool.push( ...others );
  }
  while ( picked.length < limit && pool.length ) {
    let fresh: { index: number; reason: Reason } | null = null;
    for ( let index = 0; index < pool.length && !fresh; index++ ) {
      const reason = pool[ index ].reasons.find( r => !used.has( keyOf( r ) ) );
      if ( reason ) fresh = { index, reason };
    }
    if ( fresh ) take( fresh.index, fresh.reason );
    else take( 0, pool[ 0 ].reasons.find( r => r.kind !== 'set' ) || pool[ 0 ].reasons[ 0 ] );
  }
  return picked;
}

/** what a candidate earned per trait */
interface TraitPoints {
  theme: number;
  appearance: number;
  category: number;
  color: number;
  hue: number;
  tag: number;
}

/**
 * Every reason the listing can give, the most telling first: the set, then a
 * shared tag (the vibe: few themes share one), a shared colour, the category,
 * and last the mode (half the catalogue is dark, so "Same mode" is the reason
 * of last resort; owner, 2026-09-20: "Same vibe" hardly ever showed while the
 * mode and the category always came first). A listing with nothing in common
 * gives its editorial label or "Recommended".
 */
function reasonsFor( points: TraitPoints, product: Product, category: string, sharedColor: string, sharedTag: string ): Reason[] {
  const order: ( keyof TraitPoints )[] = [ 'theme', 'tag', 'color', 'hue', 'category', 'appearance' ];
  const kinds: { [ key in keyof TraitPoints ]: Reason[ 'kind' ] } = { theme: 'set', tag: 'vibe', color: 'color', hue: 'color', category: 'category', appearance: 'mode' };
  // one theme per card, so every reason relates that one theme to the cart, and names what it
  // shares (owner, 2026-09-20: not "more dark themes" over a single card, not "another" or "also",
  // which read as random): the same mode, the same category, a similar color (the shared one, or
  // the card's own main color when only the accent hues are close), the same vibe (the shared tag,
  // Madrid and Istanbul both Mediterranean)
  const colorName = sharedColor || ( product.mainColors || [] )[ 0 ] || '';
  const words: { [ key in keyof TraitPoints ]: string } = {
    theme: COMPLETE_SET,
    appearance: product.appearance ? `Same mode: ${ product.appearance }` : 'Same mode',
    category: category ? `Same category: ${ category }` : 'Same category',
    color: colorName ? `Same color: ${ colorName }` : 'Same color',
    hue: colorName ? `Same color: ${ colorName }` : 'Same color',
    tag: sharedTag ? `Same vibe: ${ sharedTag }` : 'Same vibe',
  };
  const reasons: Reason[] = [];
  for ( const key of order.filter( k => points[ k ] > 0 ) ) {
    if ( !reasons.some( r => r.kind === kinds[ key ] ) ) reasons.push( { kind: kinds[ key ], label: words[ key ] } );
  }
  return reasons.length ? reasons : [ { kind: 'pick', label: product.editorialLabel || 'Recommended' } ];
}

/** a small seeded generator (mulberry32): the same seed gives the same sequence, so one page view shuffles one way */
function seededRandom( seed: number ): () => number {
  let state = seed >>> 0;
  return () => {
    state = ( state + 0x6D2B79F5 ) >>> 0;
    let t = state;
    t = Math.imul( t ^ ( t >>> 15 ), t | 1 );
    t ^= t + Math.imul( t ^ ( t >>> 7 ), t | 61 );
    return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
  };
}

/** Fisher-Yates, in place */
function shuffle<T>( items: T[], random: () => number ): T[] {
  for ( let i = items.length - 1; i > 0; i-- ) {
    const j = Math.floor( random() * ( i + 1 ) );
    [ items[ i ], items[ j ] ] = [ items[ j ], items[ i ] ];
  }
  return items;
}

/** degrees between two colours' hues (180 when either is missing) */
function hueDistance( a?: string, b?: string ): number {
  const ha = hueOf( a );
  const hb = hueOf( b );
  if ( ha === null || hb === null ) return 180;
  const d = Math.abs( ha - hb ) % 360;
  return d > 180 ? 360 - d : d;
}

function hueOf( hex?: string ): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec( ( hex || '' ).trim() );
  if ( !m ) return null;
  const r = parseInt( m[ 1 ].slice( 0, 2 ), 16 ) / 255;
  const g = parseInt( m[ 1 ].slice( 2, 4 ), 16 ) / 255;
  const b = parseInt( m[ 1 ].slice( 4, 6 ), 16 ) / 255;
  const max = Math.max( r, g, b );
  const min = Math.min( r, g, b );
  const delta = max - min;
  if ( delta === 0 ) return null;
  let h: number;
  if ( max === r ) h = ( ( g - b ) / delta ) % 6;
  else if ( max === g ) h = ( b - r ) / delta + 2;
  else h = ( r - g ) / delta + 4;
  h = Math.round( h * 60 );
  return h < 0 ? h + 360 : h;
}
